/**
 * Local StudyPackage store — one package per projectId (Mesa Vision C / SDD 005).
 * Does not share packages across projects (RF-4).
 */

import { getDomainTemplate, isBriefComplete } from "@/lib/studyDomainTemplates";
import { DEMO_BRIEFS, DEMO_TITLES, demoAnnexes, seedPartsFor } from "@/lib/studySeeds";
import {
  applyPatchToParts,
  patchHasChanges,
  suggestParametricPatch,
  validateChatPatch,
} from "@/lib/studyRegen";
import type {
  DomainTemplateId,
  StudyAnnotation,
  StudyAnnotationStatus,
  StudyBrief,
  StudyPackage,
  StudyParamPatch,
  StudyPart,
} from "@/lib/studyPackageTypes";

const STUDY_KEY = "prometheus_study_packages_v1";
export const STUDY_CHANGED_EVENT = "prometheus-study-changed";
/** T9: someone outside the study (pinout diagram, chat) wants a part focused. detail: { projectId, partId } */
export const STUDY_FOCUS_EVENT = "prometheus-study-focus";

// Survives the shell mounting after the request (e.g. Mesa switches from circuito to estudio).
const pendingFocus = new Map<string, string>();

/** Returns false when the project has no study part with that id (nothing is queued). */
export function requestStudyFocus(projectId: string, partId: string): boolean {
  const pkg = getStudyPackage(projectId);
  if (!pkg?.parts.some((p) => p.id === partId)) return false;
  pendingFocus.set(projectId, partId);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(STUDY_FOCUS_EVENT, { detail: { projectId, partId } }));
  }
  return true;
}

/** Shell side: take (and clear) the pending focus for this project. */
export function consumeStudyFocus(projectId: string): string | null {
  const partId = pendingFocus.get(projectId) ?? null;
  pendingFocus.delete(projectId);
  return partId;
}

type StudyMap = Record<string, StudyPackage>;

function nowIso(): string {
  return new Date().toISOString();
}

function readMap(): StudyMap {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(STUDY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StudyMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map: StudyMap) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STUDY_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(STUDY_CHANGED_EVENT, { detail: { keys: Object.keys(map) } }));
  }
}

function emptyBrief(): StudyBrief {
  return { answers: {}, interviewComplete: false };
}

/** Create empty package for a project + template (does not persist until save). */
export function createEmptyStudyPackage(
  projectId: string,
  templateId: DomainTemplateId,
  title?: string
): StudyPackage {
  const tpl = getDomainTemplate(templateId);
  const ts = nowIso();
  return {
    version: 1,
    projectId,
    templateId,
    title: title?.trim() || tpl.labelEs,
    brief: emptyBrief(),
    parts: [],
    views: [],
    annotations: [],
    master: { format: "procedural", updatedAt: ts },
    annexRenders: [],
    approvals: {},
    createdAt: ts,
    updatedAt: ts,
  };
}

export function listStudyProjectIds(): string[] {
  return Object.keys(readMap());
}

export function getStudyPackage(projectId: string): StudyPackage | null {
  const pkg = readMap()[projectId];
  return pkg ?? null;
}

/** Load or create+persist empty package for project. */
export function ensureStudyPackage(
  projectId: string,
  templateId: DomainTemplateId = "propiedad",
  title?: string
): StudyPackage {
  const existing = getStudyPackage(projectId);
  if (existing) return existing;
  const created = createEmptyStudyPackage(projectId, templateId, title);
  saveStudyPackage(created);
  return created;
}

export function saveStudyPackage(pkg: StudyPackage): StudyPackage {
  if (!pkg.projectId?.trim()) {
    throw new Error("StudyPackage requires projectId");
  }
  const map = readMap();
  const next: StudyPackage = {
    ...pkg,
    projectId: pkg.projectId,
    brief: {
      ...pkg.brief,
      interviewComplete: isBriefComplete(pkg.templateId, pkg.brief.answers),
    },
    updatedAt: nowIso(),
  };
  map[pkg.projectId] = next;
  writeMap(map);
  return next;
}

export function deleteStudyPackage(projectId: string): boolean {
  const map = readMap();
  if (!map[projectId]) return false;
  delete map[projectId];
  writeMap(map);
  return true;
}

export function hasMasterModel(pkg: StudyPackage): boolean {
  if (pkg.master.assetRef && pkg.master.assetRef.trim()) return true;
  return pkg.parts.length > 0;
}

/** RF-3: incomplete until required interview fields filled. */
export function isStudyInterviewIncomplete(pkg: StudyPackage): boolean {
  return !isBriefComplete(pkg.templateId, pkg.brief.answers);
}

export function setStudyAnswer(
  projectId: string,
  questionId: string,
  value: string
): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return null;
  const trimmed = value.trim();
  const answers = { ...pkg.brief.answers };
  if (!trimmed) {
    delete answers[questionId];
  } else {
    answers[questionId] = {
      questionId,
      value: trimmed,
      answeredAt: nowIso(),
    };
  }
  return saveStudyPackage({
    ...pkg,
    brief: {
      answers,
      interviewComplete: isBriefComplete(pkg.templateId, answers),
    },
  });
}

export type StudySeedResult =
  | { ok: true; package: StudyPackage }
  | { ok: false; reason: "missing" | "incomplete" | "has_parts" };

/**
 * T8 / criterio 1: the interview drives the study. Builds the master model (parts + footprint)
 * from the brief; derived plan / elevation follow. Never overwrites an existing model.
 */
export function generateStudyFromBrief(projectId: string): StudySeedResult {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return { ok: false, reason: "missing" };
  if (pkg.parts.length > 0) return { ok: false, reason: "has_parts" };
  if (!isBriefComplete(pkg.templateId, pkg.brief.answers)) return { ok: false, reason: "incomplete" };
  const values = Object.fromEntries(Object.values(pkg.brief.answers).map((a) => [a.questionId, a.value]));
  const ts = nowIso();
  const saved = saveStudyPackage({
    ...pkg,
    parts: seedPartsFor(pkg.templateId, values),
    master: { ...pkg.master, format: pkg.master.assetRef ? pkg.master.format : "procedural", updatedAt: ts },
  });
  return { ok: true, package: saved };
}

/**
 * Demo for the package's template: answers only the questions still empty (user answers win),
 * attaches concept renders as annexes, then runs the same interview → model path.
 */
export function loadStudyDemo(projectId: string): StudySeedResult {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return { ok: false, reason: "missing" };
  if (pkg.parts.length > 0) return { ok: false, reason: "has_parts" };
  const ts = nowIso();
  const answers = { ...pkg.brief.answers };
  for (const [questionId, value] of Object.entries(DEMO_BRIEFS[pkg.templateId])) {
    if (!answers[questionId]?.value?.trim()) answers[questionId] = { questionId, value, answeredAt: ts };
  }
  const annexIds = new Set(pkg.annexRenders.map((a) => a.id));
  saveStudyPackage({
    ...pkg,
    title: pkg.title === getDomainTemplate(pkg.templateId).labelEs ? DEMO_TITLES[pkg.templateId] : pkg.title,
    brief: { answers, interviewComplete: isBriefComplete(pkg.templateId, answers) },
    annexRenders: [...pkg.annexRenders, ...demoAnnexes(pkg.templateId, ts).filter((a) => !annexIds.has(a.id))],
  });
  return generateStudyFromBrief(projectId);
}

/**
 * RF-20: empty project → reset template freely.
 * If package already has model/parts/views, requires `confirmReset: true`.
 */
export function changeStudyTemplate(
  projectId: string,
  templateId: DomainTemplateId,
  opts?: { confirmReset?: boolean; title?: string }
): { ok: true; package: StudyPackage } | { ok: false; reason: "needs_confirm" | "missing" } {
  const pkg = getStudyPackage(projectId);
  if (!pkg) {
    const created = ensureStudyPackage(projectId, templateId, opts?.title);
    return { ok: true, package: created };
  }
  if (pkg.templateId === templateId) {
    return { ok: true, package: pkg };
  }
  const hasContent =
    hasMasterModel(pkg) ||
    pkg.views.length > 0 ||
    pkg.annotations.length > 0 ||
    Object.keys(pkg.brief.answers).length > 0;
  if (hasContent && !opts?.confirmReset) {
    return { ok: false, reason: "needs_confirm" };
  }
  const reset = createEmptyStudyPackage(projectId, templateId, opts?.title ?? pkg.title);
  reset.createdAt = pkg.createdAt;
  const saved = saveStudyPackage(reset);
  return { ok: true, package: saved };
}

export function upsertStudyParts(projectId: string, parts: StudyPart[]): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return null;
  return saveStudyPackage({ ...pkg, parts });
}

export function setStudyApprovals(
  projectId: string,
  patch: Partial<StudyPackage["approvals"]>
): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return null;
  return saveStudyPackage({
    ...pkg,
    approvals: { ...pkg.approvals, ...patch },
  });
}

export function addStudyAnnotation(
  projectId: string,
  partIds: string[],
  text: string
): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return null;
  const ts = nowIso();
  const annotation: StudyAnnotation = {
    id: `ann-${ts.replace(/[:.]/g, "")}-${Math.random().toString(36).slice(2, 7)}`,
    partIds: [...partIds],
    text: text.trim(),
    status: "pending",
    createdAt: ts,
    updatedAt: ts,
  };
  if (!annotation.text || annotation.partIds.length === 0) return pkg;
  return saveStudyPackage({
    ...pkg,
    annotations: [...pkg.annotations, annotation],
  });
}

/**
 * Annotate queue transitions (SDD 005 T6). Only `annotations` change here —
 * parts / views / master stay untouched until a confirmed regen (RF-14, RF-15).
 *   pending ⇄ proposed (request regen / cancel proposal)
 *   pending | proposed → cancelled (discard)
 *   proposed → applied is reserved for the T7 regen path.
 */
const ANNOTATION_TRANSITIONS: Record<StudyAnnotationStatus, StudyAnnotationStatus[]> = {
  pending: ["proposed", "cancelled"],
  proposed: ["pending", "cancelled", "applied"],
  applied: [],
  cancelled: ["pending"],
  orphan: ["cancelled"],
};

export type StudyAnnotationPatch = Partial<Pick<StudyAnnotation, "status" | "regenPath" | "proposalNote" | "patch">>;

export function updateStudyAnnotation(
  projectId: string,
  annotationId: string,
  patch: StudyAnnotationPatch
): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return null;
  const current = pkg.annotations.find((a) => a.id === annotationId);
  if (!current) return pkg;
  if (patch.status && patch.status !== current.status) {
    if (!ANNOTATION_TRANSITIONS[current.status].includes(patch.status)) return pkg;
  }
  const next: StudyAnnotation = { ...current, ...patch, updatedAt: nowIso() };
  // Leaving "proposed" drops the triage so a later request re-evaluates the parts' params.
  if (next.status === "pending" || next.status === "cancelled") {
    delete next.regenPath;
    delete next.patch;
    delete next.proposalNote;
  }
  return saveStudyPackage({
    ...pkg,
    annotations: pkg.annotations.map((a) => (a.id === annotationId ? next : a)),
  });
}

/** RF-15b triage, decided at proposal time: parametric only when every annotated part has knobs. */
export function proposeStudyRegen(projectId: string, annotationId: string): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  const ann = pkg?.annotations.find((a) => a.id === annotationId);
  if (!pkg || !ann) return pkg;
  const parts = ann.partIds.map((id) => pkg.parts.find((p) => p.id === id));
  const parametric = parts.length > 0 && parts.every((p) => p?.params && p.params.length > 0);
  return updateStudyAnnotation(projectId, annotationId, {
    status: "proposed",
    regenPath: parametric ? "parametric" : "chat",
    // Chat path starts empty: the copilot marker (or the manual knobs) fills it.
    patch: parametric ? suggestParametricPatch(pkg, ann) : undefined,
  });
}

/** Edits one row of a proposal before confirm (or adds the manual w/d/h rows on the chat path). */
export function setStudyProposalPatch(
  projectId: string,
  annotationId: string,
  patch: StudyParamPatch[]
): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  const ann = pkg?.annotations.find((a) => a.id === annotationId);
  if (!pkg || !ann || ann.status !== "proposed") return pkg;
  return updateStudyAnnotation(projectId, annotationId, { patch });
}

/** Chat path: attach a validated copilot proposal. Searches every package for the annotation id. */
export function attachChatStudyPatch(payload: unknown): { ok: true; projectId: string } | { ok: false; reason: string } {
  const annotationId = (payload as { annotationId?: unknown })?.annotationId;
  const projectId = listStudyProjectIds().find((id) =>
    getStudyPackage(id)?.annotations.some((a) => a.id === annotationId)
  );
  if (!projectId) return { ok: false, reason: "annotation_not_found" };
  const pkg = getStudyPackage(projectId)!;
  const result = validateChatPatch(pkg, payload);
  if (result.ok === false) return result;
  updateStudyAnnotation(projectId, result.annotationId, {
    patch: result.patch,
    proposalNote: result.note,
  });
  return { ok: true, projectId };
}

/**
 * RF-14: the only path that mutates the model from annotate. Parts + annotation status are saved
 * in one write; derived views / procedural masses follow the new params automatically.
 */
export function applyStudyRegen(projectId: string, annotationId: string): StudyPackage | null {
  const pkg = getStudyPackage(projectId);
  const ann = pkg?.annotations.find((a) => a.id === annotationId);
  if (!pkg || !ann || ann.status !== "proposed" || !patchHasChanges(ann.patch)) return null;
  if (isStudyAnnotationOrphan(pkg, ann)) return null;
  const ts = nowIso();
  return saveStudyPackage({
    ...pkg,
    parts: applyPatchToParts(pkg.parts, ann.patch!),
    master: { ...pkg.master, updatedAt: ts },
    annotations: pkg.annotations.map((a) =>
      a.id === annotationId ? { ...a, status: "applied", updatedAt: ts } : a
    ),
  });
}

/** Annotations whose parts were removed from the model (computed, not persisted). */
export function isStudyAnnotationOrphan(pkg: StudyPackage, ann: StudyAnnotation): boolean {
  return ann.partIds.some((id) => !pkg.parts.some((p) => p.id === id));
}

/** OS-sync push: every local package, keyed by projectId. */
export function exportAllStudyPackages(): Record<string, StudyPackage> {
  return readMap();
}

export type StudyMergeResult = { added: number; updated: number; keptLocal: number; skipped: number };

/**
 * OS-sync pull: per project the newer `updatedAt` wins; local-only packages are never dropped
 * (unlike the rest of the bundle, a study can hold offline work). Malformed entries are skipped.
 */
export function mergeRemoteStudyPackages(remote: unknown): StudyMergeResult {
  const result: StudyMergeResult = { added: 0, updated: 0, keptLocal: 0, skipped: 0 };
  if (!remote || typeof remote !== "object") return result;
  const map = readMap();
  let changed = false;
  for (const [projectId, raw] of Object.entries(remote as Record<string, unknown>)) {
    const pkg = raw as Partial<StudyPackage> | null;
    const valid =
      pkg &&
      pkg.version === 1 &&
      pkg.projectId === projectId &&
      typeof pkg.updatedAt === "string" &&
      Array.isArray(pkg.parts) &&
      Array.isArray(pkg.annotations);
    if (!valid) {
      result.skipped++;
      continue;
    }
    const local = map[projectId];
    if (!local) {
      map[projectId] = pkg as StudyPackage;
      result.added++;
      changed = true;
    } else if (Date.parse(pkg.updatedAt!) > Date.parse(local.updatedAt)) {
      map[projectId] = pkg as StudyPackage;
      result.updated++;
      changed = true;
    } else {
      result.keptLocal++;
    }
  }
  if (changed) writeMap(map);
  return result;
}

export function exportStudyPackageJson(projectId: string): string | null {
  const pkg = getStudyPackage(projectId);
  if (!pkg) return null;
  return JSON.stringify(pkg, null, 2);
}

export function importStudyPackageJson(json: string): StudyPackage {
  const parsed = JSON.parse(json) as StudyPackage;
  if (!parsed?.projectId || parsed.version !== 1) {
    throw new Error("Invalid StudyPackage JSON");
  }
  return saveStudyPackage(parsed);
}

/** Smoke helper for T1 verification (no UI). */
export function __smokeStudyStoreRoundTrip(): {
  templates: DomainTemplateId[];
  projectA: string;
  projectB: string;
  isolated: boolean;
  briefIncomplete: boolean;
} {
  const idA = `__smoke_casa_${Date.now()}`;
  const idB = `__smoke_coffee_${Date.now()}`;
  const a = ensureStudyPackage(idA, "propiedad", "Casa de sueños smoke");
  const b = ensureStudyPackage(idB, "propiedad", "Coffee smoke");
  setStudyAnswer(idA, "prop_program", "2 dorms + taller + terraza");
  const a2 = getStudyPackage(idA)!;
  const b2 = getStudyPackage(idB)!;
  const isolated = a2.brief.answers.prop_program?.value !== b2.brief.answers.prop_program?.value;
  const templates = listDomainTemplateIds();
  const briefIncomplete = isStudyInterviewIncomplete(a2);
  deleteStudyPackage(idA);
  deleteStudyPackage(idB);
  return {
    templates,
    projectA: a.projectId,
    projectB: b.projectId,
    isolated,
    briefIncomplete,
  };
}

function listDomainTemplateIds(): DomainTemplateId[] {
  return ["propiedad", "producto", "mecanismo", "pinout"];
}
